import { type FC, useState } from "react"
import { Link } from "react-router"

import { type BokaderoVideo, type BokaderoVideoWatch } from "@forento/core/models/bokadero"
import { cn, tw } from "@forento/ui/utilities/tailwind"

import Error from "~/components/error"
import Layout from "~/components/layout"
import Loader from "~/components/loader"
import { query } from "~/utilities/trpc"

const VideosPage: FC = () => {
	const [activeTab, setActiveTab] = useState<"videos" | "watches">("videos")
	const { data: videos, error: videosError } = query.listVideos.useQuery()
	const { data: watches, error: watchesError } = query.listVideoWatches.useQuery()

	return (
		<Layout>
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-10">
				<h1 className="text-3xl font-semibold text-slate-900">Videor</h1>

				<div className="flex gap-2 border-b border-slate-200">
					<button
						type="button"
						onClick={() => setActiveTab("videos")}
						className={cn(
							"px-4 py-2 font-medium transition-colors",
							activeTab === "videos"
								? "border-primary text-primary border-b-2"
								: "text-slate-600 hover:text-slate-900",
						)}
					>
						Videor
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("watches")}
						className={cn(
							"px-4 py-2 font-medium transition-colors",
							activeTab === "watches"
								? "border-primary text-primary border-b-2"
								: "text-slate-600 hover:text-slate-900",
						)}
					>
						Visningar
					</button>
				</div>

				{activeTab === "videos" &&
					(videosError ? (
						<Error message="Kunde inte ladda videor" />
					) : videos === undefined ? (
						<Loader label="Laddar videor..." />
					) : videos.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-10">
							<span className="text-lg font-medium text-slate-600">Inga videor tillgängliga</span>
							<span className="text-sm text-slate-400">
								Videor som tilldelats till dig kommer att visas här
							</span>
						</div>
					) : (
						<div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
							{videos.map(video => (
								<Video key={video.id} {...video} />
							))}
						</div>
					))}

				{activeTab === "watches" &&
					(watchesError ? (
						<Error message="Kunde inte ladda visningar" />
					) : watches === undefined ? (
						<Loader label="Laddar visningar..." />
					) : watches.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-10">
							<span className="text-lg font-medium text-slate-600">Inga visningar</span>
							<span className="text-sm text-slate-400">
								När användare tittar på dina videor kommer deras visningar att visas här
							</span>
						</div>
					) : (
						<div className="space-y-6">
							{watches.map(watchData => (
								<VideoWatchCard key={watchData.video.id} watchData={watchData} />
							))}
						</div>
					))}
			</div>
		</Layout>
	)
}

const videoContainerClasses = tw`group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm`

const Video: FC<BokaderoVideo> = ({ id, title, shortDescription, thumbnailFilePath, createDate }) => (
	<Link
		to={`/videos/${id}`}
		className={cn(videoContainerClasses, "cursor-pointer transition hover:-translate-y-0.5 hover:shadow-lg")}
	>
		<Thumbnail url={thumbnailFilePath} title={title} />
		<h2 className="group-hover:text-primary text-xl font-semibold text-slate-900 transition-colors">{title}</h2>
		{shortDescription && <p className="mt-3 text-base text-slate-600">{shortDescription}</p>}
		<div className="mt-6 flex items-center justify-between">
			<span className="text-sm text-slate-400">{new Date(createDate).toLocaleDateString("sv-SE")}</span>
			<div className="text-primary font-medium transition-colors">Visa mer</div>
		</div>
	</Link>
)

const Thumbnail: FC<{ url: string | null; title: string }> = ({ url, title }) => (
	<div className="mb-4 flex justify-center">
		{url ? (
			<div className="flex aspect-video w-full max-w-full items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
				<img src={url} alt={`Thumbnail för ${title}`} className="h-full w-full object-cover" />
			</div>
		) : (
			<div className="flex aspect-video w-full max-w-full items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-300">
				<span className="text-4xl">🎥</span>
			</div>
		)}
	</div>
)

const VideoWatchCard: FC<{ watchData: BokaderoVideoWatch }> = ({ watchData }) => (
	<div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
		<div className="border-b border-slate-200 p-6">
			<div className="flex items-start gap-4">
				{watchData.video.thumbnailFilePath && (
					<img
						src={watchData.video.thumbnailFilePath}
						alt={watchData.video.title}
						className="h-16 w-16 rounded-lg object-cover"
					/>
				)}
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-slate-900">{watchData.video.title}</h3>
					<p className="mt-1 text-sm text-slate-600">
						{watchData.watches.length} {watchData.watches.length === 1 ? "visning" : "visningar"}
					</p>
				</div>
			</div>
		</div>

		{watchData.watches.length > 0 && (
			<div className="space-y-3 p-6">
				{watchData.watches.map((watch, index) => (
					<div key={index} className="rounded-lg bg-slate-50 p-4">
						<p className="font-medium text-slate-900">
							{watch.user.firstName} {watch.user.lastName}
						</p>
						<p className="mb-2 text-sm text-slate-600">{watch.user.email}</p>
						<div className="flex items-center gap-2">
							<div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
								<div
									className="bg-primary h-full transition-all"
									style={{ width: `${Math.round(watch.progress * 100)}%` }}
								/>
							</div>
							<span className="text-sm font-medium text-slate-700">
								{Math.round(watch.progress * 100)}%
							</span>
						</div>
					</div>
				))}
			</div>
		)}
	</div>
)

export default VideosPage
